<script setup lang="ts">
import { computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import VChart from 'vue-echarts'
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { BoxplotChart } from 'echarts/charts'
import {
    GridComponent,
    TooltipComponent,
    TitleComponent,
} from 'echarts/components'
import type { EChartsOption } from 'echarts'
import { useI18n } from 'vue-i18n'
import { ISensorGroup } from '@/interfaces/stores/anomalyDetector'
import { useAnomalyDetectorStore } from '@/store/anomaly_detector'
import { fiveNumberSummaryFromMedianIqr } from '@/utils/anomaly-detector-boxplot-from-median-iqr'

use([CanvasRenderer, BoxplotChart, GridComponent, TooltipComponent, TitleComponent])

const { t } = useI18n()

const anomalyStore = useAnomalyDetectorStore()
const { getFieldStatistics, isFieldStatisticsLoading } = storeToRefs(anomalyStore)

const props = withDefaults(
    defineProps<{
        group: ISensorGroup
        type: 'attention' | 'critical'
        selectedDataField: string
        height?: string
        showTitle?: boolean
    }>(),
    {
        height: 'h-72',
        showTitle: true,
    }
)

const emit = defineEmits<{
    (e: 'update:selectedDataField', value: string): void
}>()

const selectedRow = computed(() => props.group.fields.find((f) => f.data_field === props.selectedDataField))

const yearBox = computed(() => {
    const row = selectedRow.value
    if (!row) return null
    return fiveNumberSummaryFromMedianIqr(row.base_median, row.base_iqr)
})

const weekBox = computed(() => {
    const row = selectedRow.value
    if (!row) return null
    const stats = getFieldStatistics.value[props.selectedDataField]
    if (
        stats &&
        stats.min != null &&
        stats.Q1 != null &&
        stats.median != null &&
        stats.Q3 != null &&
        stats.max != null
    ) {
        return {
            min: stats.min,
            Q1: stats.Q1,
            median: stats.median,
            Q3: stats.Q3,
            max: stats.max,
        }
    }
    return fiveNumberSummaryFromMedianIqr(row.week_median, row.week_iqr)
})

const hasChartData = computed(() => {
    const y = yearBox.value
    const w = weekBox.value
    return (
        y != null &&
        w != null &&
        y.min != null &&
        y.Q1 != null &&
        y.median != null &&
        y.Q3 != null &&
        y.max != null &&
        w.min != null &&
        w.Q1 != null &&
        w.median != null &&
        w.Q3 != null &&
        w.max != null
    )
})

const accentColor = computed(() => (props.type === 'critical' ? '#ef4444' : '#f97316'))
const yearFill = '#dbeafe'
const yearBorder = '#2563eb'

const yAxisConfig = computed(() => {
    const y = yearBox.value
    const w = weekBox.value
    if (!y || !w) return {}
    
    const allValues = [
        y.min, y.Q1, y.median, y.Q3, y.max,
        w.min, w.Q1, w.median, w.Q3, w.max
    ].filter((v): v is number => v != null)
    
    if (allValues.length === 0) return {}
    
    const min = Math.min(...allValues)
    const max = Math.max(...allValues)
    const range = max - min
    
    const padding = range * 0.1
    
    return {
        min: Math.floor(min - padding),
        max: Math.ceil(max + padding),
        splitNumber: 5
    }
})

const chartOption = computed<EChartsOption>(() => {
    const y = yearBox.value!
    const w = weekBox.value!

    const yearValues: [number, number, number, number, number] = [y.min!, y.Q1!, y.median!, y.Q3!, y.max!]
    const weekValues: [number, number, number, number, number] = [w.min!, w.Q1!, w.median!, w.Q3!, w.max!]

    return {
        backgroundColor: '#fff',
        title: props.showTitle ? {
            text: props.group.sensorName,
            subtext: props.selectedDataField
                ? `${t('anomaly_detector.boxplot.field_prefix')} ${props.selectedDataField} — ${t('anomaly_detector.boxplot.comparison_subtitle')}`
                : '',
            left: 'center',
            textStyle: { fontSize: 16, fontWeight: 'bold', color: '#1e293b' },
            subtextStyle: { fontSize: 12, color: '#64748b' },
        } : undefined,
        tooltip: {
            trigger: 'item',
            showContent: true,
            axisPointer: { type: 'shadow' },
            valueFormatter: (value: unknown) => {
                if (Array.isArray(value)) {
                    return value.map((v) => Number(v).toFixed(2)).join(', ')
                }
                const n = Number(value)
                return Number.isFinite(n) ? n.toFixed(2) : String(value)
            },
        },
        grid: { left: '15%', right: '10%', bottom: '15%', top: props.showTitle ? '22%' : '12%' },
        xAxis: {
            type: 'category',
            data: [t('anomaly_detector.boxplot.base_period'), t('anomaly_detector.boxplot.week')],
            axisLabel: {
                fontSize: 11,
                color: '#334155',
                interval: 0,
                formatter: (value: string) => value.replace(' do ', '\ndo '),
            },
            axisTick: { alignWithLabel: true },
        },
        yAxis: {
            type: 'value',
            ...yAxisConfig.value,
            axisLabel: { fontSize: 11, color: '#334155' },
            splitLine: { lineStyle: { color: '#e2e8f0' } },
        },
        series: [
            {
                name: 'Boxplot',
                type: 'boxplot',
                barWidth: '60%',
                data: [
                    {
                        value: yearValues,
                        name: t('anomaly_detector.boxplot.base_period'),
                        itemStyle: {
                            color: yearFill,
                            borderColor: yearBorder,
                            borderWidth: 2,
                        },
                    },
                    {
                        value: weekValues,
                        name: t('anomaly_detector.boxplot.week'),
                        itemStyle: {
                            color: props.type === 'critical' ? '#fecaca' : '#fed7aa',
                            borderColor: accentColor.value,
                            borderWidth: 2,
                        },
                    },
                ],
            },
        ],
    }
})

watch(
    () => [props.group.fields.map((f) => f.data_field).join('\0'), props.selectedDataField] as const,
    () => {
        if (props.group.fields.length === 0) return
        const valid = props.group.fields.some((f) => f.data_field === props.selectedDataField)
        if (!valid) {
            emit('update:selectedDataField', props.group.fields[0].data_field)
        }
    },
    { immediate: true }
)
</script>

<template>
    <div v-if="isFieldStatisticsLoading" class="flex items-center justify-center h-64 text-gray-500 text-sm">
        <i class="pi pi-spinner pi-spin mr-2"></i>
        {{ t('anomaly_detector.boxplot.loading_statistics') }}
    </div>

    <div v-else-if="!hasChartData" class="flex items-center justify-center h-64 text-gray-400 text-sm">
        {{ t('anomaly_detector.boxplot.insufficient_data') }}
    </div>

    <VChart
        v-else
        :option="chartOption"
        :class="['w-full', height]"
        autoresize
    />
</template>
